import { useState } from 'react'

import { initials } from './gameModel.js'
import { headshotUrl, logoUrl } from './images.js'

// A team's logo and a player's face, each drawn over the stand-in the design made for it. P6-328b3.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE PICTURE IS ALWAYS THE LAST THING THAT ARRIVES, AND THE PAGE IS FINISHED WITHOUT IT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design drew every team as a disc in its colour carrying its abbreviation, and every leader as a
// striped disc carrying initials, and called both stand-ins for a picture. Jordan wants the pictures
// (`images.js` is where they come from and what that costs). They come from somebody else's CDN, so
// each mark has three honest states and the stand-in is the first AND the last of them:
//
//   pending   the stand-in is drawn and the picture is on its way (invisible until it has arrived)
//   loaded    the picture is drawn, and the stand-in is GONE: a logo has transparent corners, and a
//             coloured disc showing through behind one is a third thing neither the design nor the NBA drew
//   failed    the picture fired `error`, so it is not in the document any more and the stand-in is what
//             is there: a refused or slow CDN still reads finished, never as a hole or a broken-image box
//
// ⚠️ A MARK HAS ITS OWN SIZE, AND THE PICTURE IS ABSOLUTELY POSITIONED INSIDE IT. That is what makes
// "nothing shifts when it loads" true by construction and not by the picture happening to be the right
// shape: the wrapper is the same box in all three states, and `<img width height>` are there as well
// because the brief asks for an explicit size and because an image with neither is the classic shift.
// `MARK_PX` and `FACE_PX` are the stylesheet's numbers (`hoops-styles.test.jsx` asserts they agree).
//
// ⚠️ EVERY `<img>` THE PAGE DRAWS IS MADE BY `Picture` BELOW, so a policy cannot be missed on one of them:
// `referrerPolicy="no-referrer"` (the portal's address is never sent to nba.com; it is the policy every
// other hotlinked picture on the site already has, in `AvatarCircle`, `MoviePanel`, `MusicPanel` and
// `WatchPanel`), `loading="lazy"` and `decoding="async"` (a board of games below the fold is not fetched
// until it is near), and `alt=""` (the team's or the player's name is written beside every one of them, so
// the picture is decoration).

/** The sizes a team's mark is drawn at, in px: `.d2d-hoops__mark` and its `--small`, `--large`, `--xl`, `--huge`.
 * (`xl` is the season views' team page, P6-329c: 52px, between the leaders' 40 and the game view's 76.) */
export const MARK_PX = Object.freeze({ small: 26, default: 32, large: 40, xl: 52, huge: 76 })

/** A face is drawn in a 38px ring with a 2px border, so the picture inside it is 34px. */
export const FACE_RING_PX = 38
export const FACE_PX = 34

/**
 * The faces' sizes by name: the ring, and the picture inside it (the ring less its 2px border on each side).
 * `large` is the player page's hero (P6-329c, the design's 56px). The picture is the SAME 260x190 file at both:
 * 52px is still under a third of its width, so a bigger ring does not buy a bigger file (`images.js` says why the
 * 1040x760 one is never read). `small` (P6-330b) is the live card's five on the floor, the design's 28px ring.
 */
export const FACE_SIZES = Object.freeze({
  small: Object.freeze({ ring: 28, face: 24 }),
  default: Object.freeze({ ring: FACE_RING_PX, face: FACE_PX }),
  large: Object.freeze({ ring: 56, face: 52 }),
})

/**
 * What one remote picture is doing: `none` (there is no URL worth asking for), `pending`, `loaded`, `failed`.
 *
 * Keyed by the URL it was told about, so a mark that is handed a different team's logo starts over at
 * `pending` and does not wear the last one's verdict.
 */
function useRemotePicture(src) {
  const [seen, setSeen] = useState({ src: null, status: 'pending' })
  const status = src === null ? 'none' : seen.src === src ? seen.status : 'pending'
  return {
    status,
    onLoad: () => setSeen({ src, status: 'loaded' }),
    onError: () => setSeen({ src, status: 'failed' }),
  }
}

/** The one `<img>` of this page. See the header for why there is exactly one place that makes it. */
function Picture({ src, className, kind, px, picture }) {
  return (
    <img
      className={className}
      src={src}
      alt=""
      width={px}
      height={px}
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      draggable="false"
      data-hoops-img={kind}
      onLoad={picture.onLoad}
      onError={picture.onError}
    />
  )
}

/**
 * A team: its logo over the design's disc (the team's colour and its abbreviation).
 *
 * `team` is the board's table entry for the team (or `null`, for a team the table does not have), and
 * `tricode` is what the card says it is. `size` is one of `MARK_PX`'s keys.
 */
export function TeamMark({ team, tricode, which, size = 'default' }) {
  const src = logoUrl(team)
  const picture = useRemotePicture(src)
  // `d2d-hoops__mark` at the default size, `d2d-hoops__mark d2d-hoops__mark--large` at the others: the disc
  // inside carries the same modifier, so the stand-in is exactly the box the picture will fill.
  const sized = (base) => (size === 'default' ? base : `${base} ${base}--${size}`)
  return (
    <span className={sized('d2d-hoops__mark')} data-state={picture.status} aria-hidden="true">
      {picture.status === 'loaded' ? null : (
        <span className={sized('d2d-hoops__disc')} data-side={which}>
          {tricode}
        </span>
      )}
      {src === null || picture.status === 'failed' ? null : (
        <Picture src={src} className="d2d-hoops__logo" kind="logo" px={MARK_PX[size]} picture={picture} />
      )}
    </span>
  )
}

/**
 * A player: his headshot in the ring the design drew, over the striped disc with his initials.
 *
 * `player` is a box-score player (`person_id` and `name`). The picture is the SMALL one, built from the
 * `person_id` (`images.js` says why and what the bot publishes instead).
 */
export function Headshot({ player, which, size = 'default' }) {
  const src = headshotUrl(player?.person_id)
  const picture = useRemotePicture(src)
  // A size this file does not know is the default one, not a crash and not a picture of no size.
  const named = Object.hasOwn(FACE_SIZES, size) ? size : 'default'
  const className = named === 'default' ? 'd2d-hoops__headshot' : `d2d-hoops__headshot d2d-hoops__headshot--${named}`
  return (
    <span className={className} data-side={which} data-state={picture.status} aria-hidden="true">
      {picture.status === 'loaded' ? null : initials(player?.name)}
      {src === null || picture.status === 'failed' ? null : (
        <Picture src={src} className="d2d-hoops__face" kind="face" px={FACE_SIZES[named].face} picture={picture} />
      )}
    </span>
  )
}
